/**
 * Parser V2: Optimized parser with smart defaults and better auto-layout
 * Focuses on making position/size optional with bulletproof auto-layout
 */

import { Node, Edge } from '@xyflow/react'
import type {
  DiagramData,
  DiagramNode,
  DiagramEdge,
  Container,
  Position,
} from '../types/schema'
import { assignEdgeOffsets, type NodePosition } from './edge-offset'
import { autoAssignHandles } from './handle-assignment'
import { assignStaggeredLabelPositions } from './labelPlacement'
import { deriveForeignKeyEdges } from './foreignKeyEdges'
import { STRUCTURED_CONTENT_KEYS } from '../types/schema'

/** Pass every structured-content field (SSOT) from a DiagramNode into React Flow data. */
function passStructuredContentV2(node: any): Record<string, any> {
  const out: Record<string, any> = {}
  for (const key of STRUCTURED_CONTENT_KEYS) out[key] = node?.[key]
  return out
}

export interface ParseResult {
  nodes: Node[]
  edges: Edge[]
  containers: Container[]
}

// ============================================================================
// V2 FEATURE TYPES (Templates & Patterns)
// ============================================================================

interface NodeTemplate {
  id: string
  nodeType: string
  icon?: string
  style?: any
  metadata?: Record<string, any>
  // Any other node properties to be used as template
  [key: string]: any
}

interface NodeReplica {
  template: string
  count: number
  idPrefix: string
  labelPattern: string
  // Optional overrides
  style?: any
  metadata?: Record<string, any>
}

interface NodeFromTemplate {
  template: string
  id: string
  label: string
  // Any overrides
  [key: string]: any
}

type EdgePatternType = 'one-to-one' | 'one-to-many' | 'many-to-many' | 'chain' | 'ring'

interface EdgePattern {
  type: EdgePatternType
  source?: string
  target?: string
  sources?: string[]
  targets?: string[]
  nodes?: string[]
  label?: string
  style?: any
  animated?: boolean
}

// ============================================================================
// SMART DEFAULTS
// ============================================================================

const DEFAULTS = {
  node: {
    size: {
      service: { width: 120, height: 80 },
      database: { width: 150, height: 100 },
      queue: { width: 140, height: 80 },
      storage: { width: 140, height: 90 },
      loadbalancer: { width: 160, height: 80 },
      default: { width: 120, height: 120 },
    },
  },
  container: {
    size: { width: 400, height: 300 },
    padding: {
      top: 80,    // Extra space for header
      side: 50,   // Left/Right
      bottom: 50, // Bottom
    },
    minSize: { width: 300, height: 250 },
  },
}

/**
 * Get smart default size based on node type
 */
function getDefaultNodeSize(nodeType: string): { width: number; height: number } {
  return DEFAULTS.node.size[nodeType as keyof typeof DEFAULTS.node.size] || DEFAULTS.node.size.default
}

// ============================================================================
// AUTO-BOUNDS CALCULATION (Enhanced from v1)
// ============================================================================

/**
 * Calculate automatic bounds for containers/shapes based on their children
 * Enhanced to handle missing child positions gracefully
 */
function calculateElementBounds(
  element: Container | DiagramNode,
  allNodes: DiagramNode[]
): { position: Position; size: { width: number; height: number } } | null {
  // Check if metadata.for or children array exists
  const childIds = element.metadata?.for || (element as any).children
  if (!childIds || !Array.isArray(childIds) || childIds.length === 0) {
    return null // No auto-bounds needed
  }

  // Get child nodes
  const children = allNodes.filter(node => childIds.includes(node.id))
  if (children.length === 0) {
    return null // No valid children found
  }

  // If any child is missing position, we can't calculate bounds yet
  // Let auto-layout handle this first
  const allChildrenHavePositions = children.every(child => child.position && child.position.x !== undefined && child.position.y !== undefined)
  if (!allChildrenHavePositions) {
    return null // Auto-layout will run first, then bounds can be calculated
  }

  // Calculate bounding box from child positions
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  children.forEach(child => {
    const x = child.position!.x
    const y = child.position!.y
    const defaultSize = getDefaultNodeSize(child.nodeType)
    const width = child.size?.width || defaultSize.width
    const height = child.size?.height || defaultSize.height

    // Calculate bounds accounting for node size (center point)
    minX = Math.min(minX, x - width / 2)
    maxX = Math.max(maxX, x + width / 2)
    minY = Math.min(minY, y - height / 2)
    maxY = Math.max(maxY, y + height / 2)
  })

  const { padding, minSize } = DEFAULTS.container

  const containerX = minX - padding.side
  const containerY = minY - padding.top
  const containerWidth = (maxX - minX) + (padding.side * 2)
  const containerHeight = (maxY - minY) + padding.top + padding.bottom

  return {
    position: {
      x: containerX,
      y: containerY
    },
    size: {
      width: Math.max(containerWidth, minSize.width),
      height: Math.max(containerHeight, minSize.height)
    }
  }
}

// ============================================================================
// TEMPLATE & PATTERN EXPANSION
// ============================================================================

/**
 * Expand nodeReplicas into actual DiagramNode objects
 * Example: { template: "web", count: 3, idPrefix: "web-", labelPattern: "Web {n}" }
 * Creates: web-1, web-2, web-3 with labels "Web 1", "Web 2", "Web 3"
 */
function expandNodeReplicas(
  replicas: NodeReplica[],
  templates: NodeTemplate[]
): DiagramNode[] {
  const nodes: DiagramNode[] = []

  replicas.forEach(replica => {
    const template = templates.find(t => t.id === replica.template)
    if (!template) {
      console.warn(`Template "${replica.template}" not found for replica`)
      return
    }

    for (let n = 1; n <= replica.count; n++) {
      const nodeId = `${replica.idPrefix}${n}`
      const label = replica.labelPattern.replace('{n}', String(n)).replace('{i}', String(n - 1))

      nodes.push({
        id: nodeId,
        label,
        nodeType: template.nodeType,
        icon: template.icon,
        style: { ...template.style, ...replica.style },
        metadata: { ...template.metadata, ...replica.metadata },
        // Spread any other template properties
        ...Object.keys(template)
          .filter(k => !['id', 'label'].includes(k))
          .reduce((acc, k) => ({ ...acc, [k]: template[k] }), {})
      } as DiagramNode)
    }
  })

  return nodes
}

/**
 * Expand nodesFromTemplate into actual DiagramNode objects
 * Example: { template: "db", id: "user-db", label: "User DB", style: {...} }
 * Inherits from template, overrides with specified properties
 */
function expandNodesFromTemplate(
  nodes: NodeFromTemplate[],
  templates: NodeTemplate[]
): DiagramNode[] {
  return nodes.map(node => {
    const template = templates.find(t => t.id === node.template)
    if (!template) {
      console.warn(`Template "${node.template}" not found`)
      // Fallback: return node without template, assuming it has required fields
      const { template: _, ...nodeWithoutTemplate } = node
      return nodeWithoutTemplate as unknown as DiagramNode
    }

    // Merge: template properties + node overrides
    const { template: _, ...nodeWithoutTemplate } = node
    return {
      ...template,
      ...nodeWithoutTemplate,
      // Deep merge style and metadata
      style: { ...template.style, ...node.style },
      metadata: { ...template.metadata, ...node.metadata }
    } as DiagramNode
  })
}

/**
 * Expand edge patterns into actual DiagramEdge objects
 * Supports: one-to-one, one-to-many, many-to-many, chain, ring
 */
function expandEdgePatterns(patterns: EdgePattern[]): DiagramEdge[] {
  const edges: DiagramEdge[] = []

  patterns.forEach(pattern => {
    switch (pattern.type) {
      case 'one-to-one':
        if (pattern.source && pattern.target) {
          edges.push({
            source: pattern.source,
            target: pattern.target,
            label: pattern.label,
            animated: pattern.animated,
            style: pattern.style
          })
        }
        break

      case 'one-to-many':
        if (pattern.source && pattern.targets) {
          pattern.targets.forEach(target => {
            edges.push({
              source: pattern.source!,
              target,
              label: pattern.label,
              animated: pattern.animated,
              style: pattern.style
            })
          })
        }
        break

      case 'many-to-many':
        if (pattern.sources && pattern.targets) {
          pattern.sources.forEach(source => {
            pattern.targets!.forEach(target => {
              edges.push({
                source,
                target,
                label: pattern.label,
                animated: pattern.animated,
                style: pattern.style
              })
            })
          })
        }
        break

      case 'chain':
        if (pattern.nodes && pattern.nodes.length >= 2) {
          for (let i = 0; i < pattern.nodes.length - 1; i++) {
            edges.push({
              source: pattern.nodes[i],
              target: pattern.nodes[i + 1],
              label: pattern.label,
              animated: pattern.animated,
              style: pattern.style
            })
          }
        }
        break

      case 'ring':
        if (pattern.nodes && pattern.nodes.length >= 2) {
          for (let i = 0; i < pattern.nodes.length - 1; i++) {
            edges.push({
              source: pattern.nodes[i],
              target: pattern.nodes[i + 1],
              label: pattern.label,
              animated: pattern.animated,
              style: pattern.style
            })
          }
          // Close the ring
          edges.push({
            source: pattern.nodes[pattern.nodes.length - 1],
            target: pattern.nodes[0],
            label: pattern.label,
            animated: pattern.animated,
            style: pattern.style
          })
        }
        break
    }
  })

  return edges
}

// ============================================================================
// MAIN PARSER
// ============================================================================

/**
 * Parse DiagramData V2 to React Flow format
 * Key improvements:
 * - Position/size are truly optional
 * - Smart defaults based on node types
 * - Better auto-layout integration
 */
export function parseDiagramData(data: DiagramData): ParseResult {
  const nodes: Node[] = []
  const edges: Edge[] = []
  const containers = data.containers || []

  // ============================================================================
  // PHASE 1C: Expand V2 Features (Templates & Patterns)
  // ============================================================================

  const templates = (data as any).nodeTemplates || []
  let diagramNodes = [...(data.nodes || [])]

  // Expand nodeReplicas (e.g., create 3 web services from template)
  if ((data as any).nodeReplicas) {
    const replicatedNodes = expandNodeReplicas((data as any).nodeReplicas, templates)
    diagramNodes = [...diagramNodes, ...replicatedNodes]
  }

  // Expand nodesFromTemplate (e.g., create nodes with template inheritance)
  if ((data as any).nodesFromTemplate) {
    const templatedNodes = expandNodesFromTemplate((data as any).nodesFromTemplate, templates)
    diagramNodes = [...diagramNodes, ...templatedNodes]
  }

  let diagramEdges = [...(data.edges || [])]

  // Expand edgePatterns (e.g., one-to-many, many-to-many)
  if ((data as any).edgePatterns) {
    const patternEdges = expandEdgePatterns((data as any).edgePatterns)
    diagramEdges = [...diagramEdges, ...patternEdges]
  }

  // ============================================================================
  // Auto-Link Children to Containers (Smart Parser Enhancement)
  // ============================================================================

  // If container has metadata.for, auto-set parentContainer on those nodes
  // This eliminates redundant parentContainer fields in JSON
  containers.forEach(container => {
    if (container.metadata?.for && Array.isArray(container.metadata.for)) {
      container.metadata.for.forEach(childId => {
        const childNode = diagramNodes.find(n => n.id === childId)
        // Only set if node exists and doesn't already have a parent
        // This respects explicit parentContainer if specified
        if (childNode && !childNode.parentContainer) {
          childNode.parentContainer = container.id
        }
      })
    }
  })

  // ============================================================================
  // Parse Expanded Data
  // ============================================================================

  // Parse containers as special background nodes
  containers.forEach((container) => {
    nodes.push(parseContainer(container, diagramNodes))
  })

  // Parse nodes (including expanded templates)
  diagramNodes.forEach((node) => {
    nodes.push(parseNode(node, data.diagramType, diagramNodes))
  })

  // Parse edges with automatic offset assignment for parallel/bidirectional connections
  if (diagramEdges) {
    const nodePositions: NodePosition[] = nodes.reduce((acc: NodePosition[], node) => {
      const position = node.position as Position | undefined
      if (
        position &&
        Number.isFinite(position.x) &&
        Number.isFinite(position.y)
      ) {
        acc.push({ id: node.id, x: position.x, y: position.y })
      }
      return acc
    }, [])

    // First, spread edges across discrete handles to avoid overlap at the node
    const edgesWithHandles = autoAssignHandles(diagramEdges, nodePositions)

    // Then assign offsets; the handle-aware grouping keeps offsets minimal when handles differ
    const edgesWithOffsets = assignEdgeOffsets(edgesWithHandles, {
      strategy: 'symmetric',  // Center offsets around 0 for balanced appearance
      nodePositions,
    })

    // Stagger default label positions so parallel edges don't stack their labels
    // (only fills in edges that have no explicit labelPosition)
    const edgesWithLabels = assignStaggeredLabelPositions(edgesWithOffsets)

    // Append column-anchored crow's-foot FK edges (same derivation as v1) so a
    // template/compressed ERD still gets relationships. Concatenated AFTER the
    // handle/offset/label passes since these carry explicit handles already.
    const fkEdges = deriveForeignKeyEdges(diagramNodes as any, diagramEdges as any)

    // Finally parse each edge (including expanded patterns, then derived FK edges)
    ;[...edgesWithLabels, ...fkEdges].forEach((edge, index) => {
      edges.push(parseEdge(edge, index))
    })
  }

  // Migrate old type names to new ones for backward compatibility
  migrateNodeTypes(nodes)

  // Convert absolute positions to relative for nodes with parentId
  convertToRelativePositions(nodes)

  return { nodes, edges, containers }
}

// ============================================================================
// TYPE MIGRATION (Same as v1)
// ============================================================================

function migrateNodeTypes(nodes: Node[]): void {
  nodes.forEach((node) => {
    if (node.type === 'universal') {
      node.type = 'icon-library'
    } else if (node.type === 'container') {
      node.type = 'group-container'
    }
  })
}

// ============================================================================
// POSITION CONVERSION (Same as v1)
// ============================================================================

function convertToRelativePositions(nodes: Node[]): void {
  const nodeMap = new Map<string, Node>()
  nodes.forEach(node => nodeMap.set(node.id, node))

  nodes.forEach(node => {
    if (node.parentId) {
      const parent = nodeMap.get(node.parentId)
      if (parent) {
        node.position = {
          x: node.position.x - parent.position.x,
          y: node.position.y - parent.position.y,
        }
      }
    }
  })
}

// ============================================================================
// CONTAINER PARSER (Enhanced position/size handling)
// ============================================================================

function parseContainer(container: Container, allNodes: DiagramNode[]): Node {
  // Try to calculate auto-bounds from metadata.for or children array
  const autoBounds = calculateElementBounds(container, allNodes)

  // KEY CHANGE: If position is missing AND layout.algorithm is set, leave undefined
  // This lets React Flow + auto-layout handle it properly
  let position: Position
  if (autoBounds?.position) {
    position = autoBounds.position
  } else if (container.position) {
    position = container.position
  } else {
    // No position specified - use 0,0 for now
    // DiagramEditor will run auto-layout if needed
    position = { x: 0, y: 0 }
  }

  const width = autoBounds?.size.width || container.size?.width || DEFAULTS.container.size.width
  const height = autoBounds?.size.height || container.size?.height || DEFAULTS.container.size.height

  return {
    id: container.id,
    type: 'group-container',
    position,
    data: {
      label: container.label,
      description: container.description,
      icon: container.icon,
      originalIcon: (container as any).originalIcon || container.icon,
      metadata: container.metadata,
      containerType: container.type,
      provider: container.metadata?.provider,
      layerId: container.layerId, // Layer assignment (draw.io-style layers)
      bgColor: container.style?.bgColor || '#f0f0f0',
      textColor: container.style?.textColor || '#000000',
      borderColor: container.style?.borderColor || '#d0d0d0',
      borderStyle: container.style?.borderStyle || 'solid',
      opacity: container.style?.opacity || 0.5,
      isDropTarget: false,
    },
    width,
    height,
    style: {
      zIndex: -10,
    },
    expandParent: container.autoResize || false,
    parentId: container.parentContainer,
    draggable: true,
    selectable: true,
    deletable: true,
  }
}

// ============================================================================
// NODE PARSER (Enhanced position/size handling)
// ============================================================================

function parseNode(node: DiagramNode, diagramType: string, allNodes: DiagramNode[]): Node {
  // Determine node type
  let nodeType = 'icon-library'

  // Check for structured content nodes FIRST (higher priority)
  let hasStructuredContent = false
  if ((node as any).tableContent) {
    nodeType = 'database-table'
    hasStructuredContent = true
  } else if ((node as any).resourceContent) {
    nodeType = 'api-resource'
    hasStructuredContent = true
  } else if ((node as any).endpointContent) {
    nodeType = 'api-endpoint'
    hasStructuredContent = true
  } else if ((node as any).classContent) {
    nodeType = 'uml-class'
    hasStructuredContent = true
  } else if ((node as any).stateContent) {
    nodeType = 'state-node'
    hasStructuredContent = true
  } else if ((node as any).c4Content) {
    nodeType = 'c4-node'
    hasStructuredContent = true
  } else if ((node as any).sequenceContent) {
    nodeType = 'sequence-lifeline' // keep explicit size (not hasStructuredContent)
  } else if ((node as any).sequenceFragment) {
    nodeType = 'sequence-fragment'
  } else if ((node as any).sequenceNote) {
    nodeType = 'sequence-note'
  } else if ((node as any).ganttContent) {
    nodeType = 'gantt-task'
  } else if ((node as any).ganttSection) {
    nodeType = 'gantt-section'
  } else if ((node as any).ganttAxis) {
    nodeType = 'gantt-axis'
  } else if ((node as any).shapeId || (node as any).isShape) {
    nodeType = 'shape'
  } else if ((node as any).nodeType === 'text') {
    nodeType = 'text'
  }

  // Try to calculate auto-bounds ONLY for shape nodes with metadata.for
  let autoBounds = null
  if (nodeType === 'shape' && node.metadata?.for) {
    autoBounds = calculateElementBounds(node, allNodes)
  }

  // KEY CHANGE: Better position handling
  let position: Position
  if (autoBounds?.position) {
    position = autoBounds.position
  } else if (node.position) {
    position = node.position
  } else {
    // No position - use 0,0 temporarily
    // Auto-layout will fix this in DiagramEditor
    position = { x: 0, y: 0 }
  }

  // KEY CHANGE: Smart size defaults based on nodeType
  // For structured content nodes, DON'T set fixed width/height - let component auto-size
  const defaultSize = getDefaultNodeSize(node.nodeType)
  const width = hasStructuredContent ? undefined : (autoBounds?.size.width || node.size?.width || defaultSize.width)
  const height = hasStructuredContent ? undefined : (autoBounds?.size.height || node.size?.height || defaultSize.height)

  return {
    id: node.id,
    type: nodeType,
    position,
    data: {
      label: node.label,
      nodeType: node.nodeType,
      subtype: node.subtype,
      diagramType,
      icon: node.icon,
      originalIcon: (node as any).originalIcon || node.icon,
      description: node.description,
      parentContainer: node.parentContainer,
      layer: node.layer,
      // Layer assignment (draw.io-style layers).
      layerId: node.layerId,
      // Rotation in degrees (applied to node visual content)
      rotation: typeof node.rotation === 'number' ? node.rotation : 0,
      shapeId: node.shapeId,
      shapeCategory: node.shapeCategory,
      isShape: node.isShape,
      metadata: node.metadata,
      customData: node.customData,
      style: node.style,

      // ✅ STRUCTURED CONTENT - pass every content field through from the SSOT.
      ...passStructuredContentV2(node),
    },
    width,
    height,
    style: {},
    // Stacking order (front/back) — read back onto React Flow's node.zIndex.
    ...(typeof node.zIndex === 'number' ? { zIndex: node.zIndex } : {}),
    // Sequence nodes use top-left origin (see parser-v1) despite the editor's
    // global center nodeOrigin, so they don't shift up by half their height.
    ...(nodeType.startsWith('sequence-') || nodeType.startsWith('gantt-') ? { origin: [0, 0] as [number, number] } : {}),
    parentId: node.parentContainer,
    draggable: true,
    selectable: true,
    deletable: true,
  }
}

// ============================================================================
// EDGE PARSER (Same as v1 for now)
// ============================================================================

function parseEdge(edge: DiagramEdge, index: number): Edge {
  const zIndexValue = (edge as any).data?.zIndex !== undefined ? (edge as any).data.zIndex : (edge as any).zIndex || 0

  return {
    id: edge.id || `edge-${edge.source}-${edge.target}-${index}`,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
    // Default to the full-featured 'custom' edge, but preserve an explicit
    // opt-in 'smart' routing choice so it survives reload/re-import.
    type: edge.type === 'smart' ? 'smart' : 'custom',
    label: edge.label,
    animated: edge.animated || false,
    zIndex: zIndexValue,
    style: {},
    data: {
      color: edge.style?.stroke || (edge as any).data?.color || '#b1b1b7',
      lineWidth: edge.style?.strokeWidth || (edge as any).data?.lineWidth || 2,
      lineStyle: (edge as any).data?.lineStyle || 'solid',
      lineCap: (edge as any).data?.lineCap || 'butt',
      lineJoin: (edge as any).data?.lineJoin || 'miter',
      opacity: (edge as any).data?.opacity !== undefined ? (edge as any).data.opacity : 1,
      arrowStart: (edge as any).data?.arrowStart || 'none',
      arrowEnd: (edge as any).data?.arrowEnd || 'arrow',
      arrowSize: (edge as any).data?.arrowSize || 1,
      bidirectional: (edge as any).data?.bidirectional || false,
      labelFontSize: (edge as any).data?.labelFontSize || 12,
      labelBackground: (edge as any).data?.labelBackground || 'white',
      labelBackgroundColor: (edge as any).data?.labelBackgroundColor || '#ffffff',
      labelZIndex: (edge as any).data?.labelZIndex || 1,
      labelPosition: (edge as any).data?.labelPosition ?? 0.5,
      labelMode: (edge as any).data?.labelMode || 'default',
      labelText: (edge as any).data?.labelText || '',
      shadowEnabled: (edge as any).data?.shadowEnabled || false,
      shadowColor: (edge as any).data?.shadowColor || '#000000',
      shadowBlur: (edge as any).data?.shadowBlur || 3,
      shadowOffsetX: (edge as any).data?.shadowOffsetX || 2,
      shadowOffsetY: (edge as any).data?.shadowOffsetY || 2,
      zIndex: zIndexValue,
      // Edge offset and direction (for parallel/bidirectional edge separation)
      offset: (edge as any).data?.offset ?? 0,  // Path offset in pixels
      edgeDirection: (edge as any).data?.edgeDirection || 'single',  // Direction in bidirectional pair
      edgeType: edge.edgeType,
      protocol: edge.protocol,
      method: edge.method,
      description: edge.description,
      metadata: edge.metadata,
      // Connector routing shape + fixed-vs-floating attachment (draw.io parity).
      pathStyle: (edge as any).data?.pathStyle,
      targetAngle: (edge as any).data?.targetAngle,
      handlePreference: (edge as any).data?.handlePreference,
      fixedConnection: (edge as any).data?.fixedConnection || false,
      // ERD: marks an edge derived from a foreign key (dedupe key + provenance).
      derivedFromFk: (edge as any).data?.derivedFromFk,
      // Layer assignment (draw.io-style layers).
      layerId: edge.layerId,
    },
  }
}

// ============================================================================
// VALIDATION
// ============================================================================

export function validateDiagramData(data: any): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!data) {
    errors.push('Diagram data is null or undefined')
    return { valid: false, errors }
  }

  if (!data.diagramType) {
    errors.push('Missing required field: diagramType')
  }

  if (!data.nodes || !Array.isArray(data.nodes)) {
    errors.push('Missing or invalid field: nodes must be an array')
  } else {
    data.nodes.forEach((node: any, index: number) => {
      if (!node.id) {
        errors.push(`Node at index ${index} is missing required field: id`)
      }
      if (!node.label) {
        errors.push(`Node at index ${index} is missing required field: label`)
      }
      // V2: nodeType is optional if we can infer from other fields
      if (!node.nodeType && !node.type && !node.shapeId) {
        errors.push(`Node at index ${index} is missing nodeType or type field`)
      }
    })
  }

  if (data.edges && !Array.isArray(data.edges)) {
    errors.push('Invalid field: edges must be an array')
  } else if (data.edges) {
    data.edges.forEach((edge: any, index: number) => {
      if (!edge.source) {
        errors.push(`Edge at index ${index} is missing required field: source`)
      }
      if (!edge.target) {
        errors.push(`Edge at index ${index} is missing required field: target`)
      }
    })
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
